import { ShallowKeys, SingleKeyObject } from '@mangovoice/types'

const getDotNotatedObjectKeys = (object: Record<string, unknown>): string[] => {
  const keys = Object.keys(object)
  const dotNotatedKeys = keys.map((key) => {
    if (typeof object[key] === 'object') {
      const subKeys = getDotNotatedObjectKeys(
        object[key] as Record<string, unknown>,
      )
      return subKeys.map((subKey) => `${key}.${subKey}`)
    }
    return key
  })

  return dotNotatedKeys.flat()
}

const buildDotNotatedObjectByKeyString = (
  parentObject: Record<string, unknown>,
  keyString: string,
  value: string,
) => {
  const keySplits = keyString.split('.')
  let object = parentObject
  for (let i = 0; i < keySplits.length; i++) {
    const key = keySplits[i]
    if (i < keySplits.length - 1) {
      if (!object[key]) {
        object[key] = {}
      }
      object = object[key] as Record<string, unknown>
      continue
    }

    object[key] = value
  }
  return object
}

export const getDotWalkedObjectMap = (
  object: Record<string, unknown>,
  depthCutoff = 0,
) => {
  const dotNotatedKeys = getDotNotatedObjectKeys(object)
  const depthCutoffKeyHashSet = new Set<string>()
  dotNotatedKeys.map((key) => {
    const keySplits = key.split('.')
    let rebuiltKey = keySplits[0]
    for (let i = 1; i < keySplits.length - depthCutoff; i++) {
      rebuiltKey = `${rebuiltKey}.${keySplits[i]}`
    }
    depthCutoffKeyHashSet.add(rebuiltKey)
  })
  const parentObject = {}
  depthCutoffKeyHashSet.forEach((key) => {
    buildDotNotatedObjectByKeyString(parentObject, key, key)
  })

  return parentObject
}

export const getShallowObjectKeyMap = (object: Record<string, unknown>) => {
  const keys = Object.keys(object)
  const keyMap: Record<string, unknown> = {}
  keys.forEach((key) => {
    keyMap[key] = key
  })
  return keyMap
}

export const getName = <T extends PropertyKey>(object: SingleKeyObject<T>) => {
  return Object.keys(object)[0]
}

export const getOutOfSyncProperties = <T extends Record<string, unknown>>(
  source: Partial<T>,
  target: Partial<T>,
): Partial<T> => {
  const outOfSyncProperties: Partial<T> = {}
  for (const key in source) {
    const sourceValue = source[key]
    const targetValue = target[key]

    const sourceIsObject = typeof sourceValue === 'object'
    const targetIsObject = typeof targetValue === 'object'
    if (sourceIsObject && targetIsObject) {
      const outOfSyncSubProperties = getOutOfSyncProperties(
        sourceValue as Record<string, unknown>,
        targetValue as Record<string, unknown>,
      )

      if (!isEmptyObject(outOfSyncSubProperties)) {
        outOfSyncProperties[key] = outOfSyncSubProperties as T[Extract<
          keyof T,
          string
        >]
      }

      continue
    }

    if (sourceValue !== targetValue) {
      outOfSyncProperties[key] = sourceValue as T[Extract<keyof T, string>]
    }
  }

  return outOfSyncProperties
}

export const isEmptyObject = (object: Record<string, unknown> | unknown) => {
  if (typeof object !== 'object') return undefined

  return Object.keys(object!).length === 0
}

export const hasKey = <T extends Record<string, unknown>>(
  object: T,
  key: ShallowKeys<T>,
) => {
  return Object.prototype.hasOwnProperty.call(object, key)
}

/**
 * Overrides the properties of the source object with the properties of the target object,
 * but keeping the reference to the source object the same as it was
 * @param sourceObject The object to be overwritten
 * @param targetObject The object to use to override the source object
 * @returns The updated source object. If the source object was falsy, the target object is returned instead
 */
export const shallowOverwrite = <T extends object>(
  sourceObject: T,
  targetObject: T,
) => {
  if (!sourceObject && targetObject) {
    return targetObject
  }
  const sourceObjectKeys = Object.keys(sourceObject)
  const targetObjectKeys = Object.keys(targetObject)

  //remove source object keys that do not exist in the target
  for (const key of sourceObjectKeys) {
    const typedKey = key as keyof T
    if (!targetObjectKeys.includes(key)) {
      delete sourceObject[typedKey]
    }
  }

  for (const key in targetObject) {
    sourceObject[key] = targetObject[key]
  }

  return sourceObject
}
